<template>
	<div class="row">
		<div class="col-12 col-lg-8">
			<div class="c c-cart">
				<h1>Winkelwagen</h1>
				<template v-if="!isLoading">
					<div v-if="products.length === 0" class="c-cart__noresults">
						<p>Uw winkelwagen bevat (nog) geen artikelen.</p>
						<div class="cxs c-buttonlist justify-content-between">
							<a class="btn btn-primary" href="/assortiment">Verder winkelen</a>
						</div>
					</div>
					<div v-else class="c-cart__inner">
						<div v-for="product in products" :key="product.Id" class="c-cartitem">
							<div class="c-cartitem__img"><img :src="getItemImage(product)" :alt="product.ItemUOM" class="img-fluid" /></div>
							<div class="c-cartitem__info">
								<div class="c-cartitem__info--title">{{ product.ItemDescription }}</div>
								<div class="c-cartitem__info--artnr">Artikelnummer: {{ product.ItemNo }}</div>
								<div v-if="!itemDeliverable(product)" class="c-cartitem__info--alert">
									<div class="c-cartitem__info--alert--item">Dit product is te groot om te verzenden</div>
								</div>
							</div>
							<div class="c-cartitem__amount">
								<div class="c-cartitem__amount--control">
									<div tabindex="0"
										 :class="['c-cartitem__amount--minus', { disabled: product.Amount < 2 }]"
										 @click="changeItemAmount(product.Id, product.Amount - 1)"
										 @keyup.enter="changeItemAmount(product.Id, product.Amount - 1)">
										<i class="fal fa-minus"></i>
										<span class="sr-only">Minder</span>
									</div>

									<div class="c-cartitem__amount--nr">
										<input type="text" pattern="[0-9]{3}" maxlength="3" :value="product.Amount" @change="updateItemAmount($event, product.Id)" />
									</div>
									<div tabindex="0"
										 :class="['c-cartitem__amount--plus', { disabled: product.Amount > 998 }]"
										 @click="changeItemAmount(product.Id, product.Amount + 1)"
										 @keyup.enter="changeItemAmount(product.Id, product.Amount + 1)">
										<i class="fal fa-plus" />
										<span class="sr-only">Meer</span>
									</div>
								</div>

								<div class="c-cartitem__amount--remove">
									<a class="btn btn-link" @click="removeItemFromBasket(product.Id)">Verwijderen</a>
								</div>
							</div>
							<div class="c-cartitem__price">
								<span class="isnumeric">{{ (product.CustomerPrice * product.Amount) | toCurrency }}</span>
							</div>
						</div>
						<div class="c-cart__summary">
							<div class="c-cart__summary--inner">
								<div class="c-cart__summary--item">
									<span>Subtotaal excl. BTW</span> <span class="isnumeric">{{ totalCustomerPriceAllProducts | toCurrency }}</span>
								</div>
								<div class="c-cart__summary--item">
									<span>Verzendkosten</span>
									<template v-if="deliverOrder.customerChoiceDelivery">
										<span v-if="orderDeliveryPrice > 0" class="isnumeric">{{ orderDeliveryPrice | toCurrency }}</span>
										<span v-else class="text-success font-weight-bold">Geen</span>
									</template>
									<span v-else class="text-success font-weight-bold">Geen</span>
								</div>
								<div class="c-cart__summary--item istotal">
									<span>Totaalbedrag <span class="small text-muted">(Excl. BTW)</span></span>
									<span v-if="deliverOrder.deliverable" class="isnumeric">{{ (totalCustomerPriceAllProducts + calcOrderDeliveryPrice) | toCurrency }}</span>
									<span v-else class="isnumeric">{{ totalCustomerPriceAllProducts | toCurrency }}</span>
								</div>
							</div>
						</div>
					</div>
				</template>
				<div v-else class="c-loader__wrapper">
					<ComponentLoader />
				</div>
			</div>
		</div>
		<div class="col-12 col-lg-4">
			<div v-if="products.length > 0" class="c c-cart__sidebar">
				<label :class="['c-cart__sidebar--item c-cart__sidebar--option', { disabled: !deliverOrder.deliverable }]">
					<input v-model="deliverOrder.customerChoiceDelivery" type="radio" name="delivery" :value="true" :disabled="!deliverOrder.deliverable" />
					<div class="c-cart__sidebar--option--image"><img :src="deliveryImage" alt="Foto van een bezorger" loading="lazy" /></div>
					<div class="c-cart__sidebar--option--title">Laten bezorgen</div>

					<div v-if="deliverOrder.deliverable" class="c-cart__sidebar--option--inner">

						<span v-if="orderDeliveryPrice > 0" >
						<div>Voor {{ orderDeliveryPrice | toCurrency }} thuisbezorgd</div>
						<div class="small text-success">Gratis verzending vanaf {{ deliveryTreshold | toCurrency }}</div>
						</span>
						<span v-else><strong class="text-success">Gratis</strong> thuisbezorgd</span>
					</div>
					<div v-else class="c-cart__sidebar--option--inner">
						<div class="text-muted">Bezorging niet mogelijk</div>
					</div>
				</label>
				<label class="c-cart__sidebar--item c-cart__sidebar--option">
					<input v-model="deliverOrder.customerChoiceDelivery" type="radio" name="delivery" :value="false" />
					<div class="c-cart__sidebar--option--title">Ophalen</div>
					<div class="c-cart__sidebar--option--inner"><strong class="text-success">Gratis</strong> ophalen in onze vestiging</div>
				</label>
				<div class="c-cart__sidebar--item c-cart__sidebar--btn bg-light">
					<a :href="bestellenUrl" class="btn btn-primary">Ik ga bestellen</a>
					<a href="/" class="btn btn-default">Verder winkelen</a>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import axios from 'axios';
	import ComponentLoader from '../products/components/general/ComponentLoader.vue';

	export default {
		components: {
			ComponentLoader
		},
		props: {
			basketSettings: {
				type: Object,
				required: true
			}
		},
		data: () => ({
			basketClient: null,
			basketId: null,
			products: [],
			amountInBasket: localStorage.getItem('amountInBasket'),
			isLoadingStock: true,
			warningMessage: {
				active: false,
				text: ''
			},
			isLoading: false,
			deliverOrder: {
				deliverable: true,				// whether all products are deliverable
				customerChoiceDelivery: true	// whether customer picks delivery
			},
			deliveryPrice: 17.50,
			deliveryTreshold: 500,
			deliveryImage: window.location.origin + '/Assets/Images/delivery.png'
		}),
		computed: {
			bestellenUrl() {
				if (this.deliverOrder?.customerChoiceDelivery) return "/bestellen?verzenden=" + this.deliverOrder.customerChoiceDelivery;
				else return "/bestellen/";
			},
			calcOrderDeliveryPrice() {
				if (!this.deliverOrder?.customerChoiceDelivery || this.totalCustomerPriceAllProducts > this.deliveryTreshold) {
					return 0;
				}
				return this.deliveryPrice;
			},
			orderDeliveryPrice() {
				if (this.totalCustomerPriceAllProducts > this.deliveryTreshold) {
					return 0;
				}
				return this.deliveryPrice;
			},
			totalCustomerPriceAllProducts() {
				let totalPrice = 0;

				if (this.products.length > 0) {
					for (var i = 0, _len = this.products.length; i < _len; i++) {
						totalPrice += this.products[i].TotalCustomerPrice;
					}
				}
				return this.toRoundedPrice(totalPrice);
			},
		},
		created() {
			this.basketClient = axios.create({
				baseURL: '/api'
			});
			this.basketClient.defaults.headers.common['Content-Type'] = 'application/json';
			this.basketClient.defaults.timeout = 0;
			this.basketClient.defaults.method = 'post';

			// get customer basket from ABP (via the BasketController)
			this.basketClient
				.post('/winkelwagen/index')
				.then((response) => {
					if (response.data != null && response.data !== '' && typeof response.data.BasketItems === 'object' && response.data.BasketItems.length > 0) {
						this.products = response.data.BasketItems;
						this.basketId = response.data.Id;
					}
					this.isLoading = false;
					this.canBeDelivered();
					this.updateLocalStorageAndDispatchEvent();
				})
				.catch((e) => {
					// console.error(e);
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		methods: {
			canBeDelivered: function () {
				const items = this.products.map((a) => JSON.parse(a.Data).Verzendbaar);
				if (items.includes(false)) {
					this.deliverOrder.deliverable = false;
					this.deliverOrder.customerChoiceDelivery = false;
					return false;
				}
				this.deliverOrder.deliverable = true;
				return true;
			},
			itemDeliverable: function (item) {
				return item != null ? JSON.parse(item.Data).Verzendbaar : false;
			},
			getItemImage: function (item) {
				return item != null && JSON.parse(item.Data).ImageUrl != null ? JSON.parse(item.Data).ImageUrl : '/Assets/Images/noimage.png';
			},
			removeItemFromBasket(guid) {
				if (this.products === null || this.products.length === 0 || guid === null || guid === '') {
					return;
				}

				// get item from basket (item-list)
				const productToRemove = this.products.filter((i) => i.Id === guid);
				if (productToRemove.length === 0) {
					return;
				}

				// remove item
				this.basketClient
					.post('/winkelwagen/item/remove', productToRemove[0])
					.then((response) => {
						this.removeItemFromLocalBasket(guid);
						this.canBeDelivered();
						this.updateLocalStorageAndDispatchEvent();
					})
					.catch((e) => {
						// console.error(e);
					});
			},
			removeItemFromLocalBasket(guid) {
				var productKeyToRemove = this.products.findIndex((i) => i.Id === guid);
				this.products.splice(productKeyToRemove, 1);
			},
			updateItemAmount(event, guid) {
				this.changeItemAmount(guid, event.target.value);
			},
			changeItemAmount(guid, newAmount) {
				const productKey = this.products.findIndex((i) => i.Id === guid);
				if (productKey == null) {
					return;
				}

				var amt = parseInt(newAmount);
				if (isNaN(amt) || amt < 1) {
					newAmount = 1;
				} else if (amt > 999) {
					newAmount = 999;
				}

				// change amount and total prices (first set to 0, then to new value to always trigger the @change handler)
				this.products[productKey].Amount = 0;
				this.products[productKey].Amount = newAmount;
				this.products[productKey].TotalStandardPrice = this.toRoundedPrice(this.products[productKey].Amount * parseFloat(this.products[productKey].StandardPrice));
				this.products[productKey].TotalCustomerPrice = this.toRoundedPrice(this.products[productKey].Amount * parseFloat(this.products[productKey].CustomerPrice));

				this.basketClient
					.post('/winkelwagen/item/update', this.products[productKey])
					.then((response) => {
						this.updateLocalStorageAndDispatchEvent();
					})
					.catch((e) => {
						// console.error(e);
					});
			},
			updateLocalStorageAndDispatchEvent() {
				if (this.products == null || typeof this.products === 'undefined') {
					return;
				}

				this.amountInBasket = 0;
				for (var i = 0, _len = this.products.length; i < _len; i++) {
					this.amountInBasket += this.products[i].Amount;
				}

				localStorage.setItem('amountInBasket', this.amountInBasket);
				localStorage.setItem('basketId', this.basketId);

				window.dispatchEvent(
					new CustomEvent('aquaco-basket-changed', {
						detail: {
							amount: this.amountInBasket
						}
					})
				);
			},
			toRoundedPrice(price) {
				return Math.round((price + Number.EPSILON) * 100) / 100;
			}
		}
	};
</script>
